'use client';

import type { MouseEvent } from 'react';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';

import { Sheet } from '@zoomit/mobile-sheet';
import { Box, Col, Flex, Modal, Portal, Spacing } from '@zoomit/primitives';
import { Button, cssVar, ModalActions, ModalHead, theme, Typography } from '@zoomit/zoomit-ds';

import { isMobile } from '../../constants/media-query';
import { useWebKitCustomization } from '../../hooks/useWebKitCustomization';

export const UnauthorizedBody = () => {
  const signInReasons = useWebKitCustomization((config) => config.signInReasons);

  return (
    <Box backgroundColor={cssVar(theme.card)} spacing={{ px: isMobile ? 'x12' : 'x6', pb: 'x6', pt: 'x3' }}>
      {isMobile && (
        <Title spacing={{ mb: 'x8' }} variant="h5">
          وارد شوید / ثبت‌نام کنید
        </Title>
      )}
      <Typography lineHeight={2} variant="h6">
        برای ادامه باید وارد حساب کاربری خودت بشی.
      </Typography>
      <Typography lineHeight={2} variant="body" tag="p" spacing={{ mb: 'x8' }}>
        اگر قبلا ثبت‌نام کردی که چه عالی! فقط باید وارد بشی و اگر نه کافیه در کوتاه‌ترین زمان ثبت‌نام کنی و از
        امکانات زیر بهره ببری:
      </Typography>
      <Box
        className={'grid grid-cols-1 gap-4 md:grid-cols-3 md:justify-items-center md:gap-x-5'}
        spacing={{ mt: 'x4' }}
      >
        {signInReasons.map((reason) => (
          <Flex
            key={reason.description}
            md={[{ flexDirection: 'column' }]}
            alignItems="center"
            gap={Spacing.x4}
          >
            <Col size={isMobile ? 2 : 0}>{reason.icon}</Col>
            <Col>
              <Typography
                variant="callout"
                textAlign={isMobile ? 'right' : 'center'}
                tag="p"
                lineHeight={1.85}
              >
                {reason.description}
              </Typography>
            </Col>
          </Flex>
        ))}
      </Box>
    </Box>
  );
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const UnauthorizedModal = (props: Props) => {
  const { isOpen, onClose } = props;
  const { signinRedirect } = useAuth();

  const handleSignIn = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    signinRedirect({ url_state: window.location.href });
  };

  const handleClose = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  return (
    <>
      {isOpen && !isMobile && (
        <Portal>
          <Modal onClose={onClose} isOpen={isOpen}>
            <ModalHead onClose={onClose} title="وارد شوید / ثبت‌نام کنید" />
            <UnauthorizedBody />
            <ModalActions>
              <Col size={5}>
                <Button size="full width" onClick={handleSignIn} type="submit" color="#32323c">
                  ورود / ثبت‌نام
                </Button>
              </Col>
              <Col size={4.5}>
                <Button variant="outline" onClick={handleClose}>
                  انصراف
                </Button>
              </Col>
            </ModalActions>
          </Modal>
        </Portal>
      )}
      {isMobile && (
        <Sheet isOpen={isOpen} onClose={onClose}>
          <Sheet.Container>
            <Sheet.Header>
              <Sheet.CloseIndicator />
            </Sheet.Header>
            <Sheet.Content>
              <UnauthorizedBody />
            </Sheet.Content>
            <Flex
              className="bg-background1"
              gap={Spacing.x4}
              spacing={{ px: Spacing.x11, py: Spacing.x4 }}
              justifyContent="center"
              alignItems="center"
            >
              <Col>
                <Button
                  onClick={handleSignIn}
                  type="submit"
                  color="#32323c"
                  size="full width"
                  variant="contained"
                >
                  ورود
                </Button>
              </Col>
              <Col size={4.5}>
                <Button variant="outline" onClick={handleClose}>
                  انصراف
                </Button>
              </Col>
            </Flex>
          </Sheet.Container>
          <Sheet.Backdrop />
        </Sheet>
      )}
    </>
  );
};

export default UnauthorizedModal;

const Title = styled(Typography)`
  position: relative;
  padding-right: 2rem;

  &::before {
    content: '';
    height: 2.4rem;
    width: 0.4rem;
    background-color: ${cssVar(theme.primary1)};
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
`;
const ModalBottom = styled(Flex)`
  background-color: ${cssVar(theme.background1)};
`;
